import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './Specialties.module.css';
import API_BASE_URL from '../../config/api';

import imgFlanges     from '../../assets/images/Home-page-categories/flange-screws-nuts-flange-screws-nuts-plumbing-99210454.jpg';
import imgChains      from '../../assets/images/Home-page-categories/16-مصنع-السلاسل.jpg';
import imgCnc         from '../../assets/images/Home-page-categories/kisspng-rack-and-pinion-gear-train-sprocket-5b0811f58416c1.9586763815272555415411.png';
import imgLaser       from '../../assets/images/Home-page-categories/Laser-Technology.jpg';
import imgAutomotive  from '../../assets/images/Home-page-categories/nwnwDSC_5326666jjj7.jpg';
import imgRailway     from '../../assets/images/Home-page-categories/DSC_535126622.jpg';
import imgForgedParts from '../../assets/images/Home-page-categories/WhatsApp-Image-2021-01-21-at-12.56.22-PM.jpeg';
import imgConveyors   from '../../assets/images/Home-page-categories/Untitled.png';

const SLUG_FALLBACK = {
  'flanges':            imgFlanges,
  'chain-plant':        imgChains,
  'cnc-parts':          imgCnc,
  'laser-cutting':      imgLaser,
  'automotive-tractor': imgAutomotive,
  'railway-needs':      imgRailway,
  'forged-parts':       imgForgedParts,
  'conveyors':          imgConveyors,
};

const stripHtml = (html) => {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

const getVisibleCount = () => {
  const w = window.innerWidth;
  if (w < 480)  return 1;
  if (w < 768)  return 2;
  if (w < 1100) return 3;
  return 4;
};

const Specialties = () => {
  const { t, i18n } = useTranslation();
  const [products, setProducts]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(getVisibleCount);
  const [cardWidth, setCardWidth]     = useState(0);

  const viewportRef  = useRef(null);
  const isHovering   = useRef(false);
  const autoPlayCb   = useRef(null);

  /* ── fetch products ──────────────────────────────────────────── */
  useEffect(() => {
    const controller = new AbortController();
    const lang = i18n.language === 'ar' ? 'ar' : 'en';

    fetch(`${API_BASE_URL}/products?lang=${lang}`, { signal: controller.signal })
      .then(r => r.json())
      .then(json => {
        const items = Array.isArray(json) ? json : json.data || [];
        setProducts(items.map(item => ({
          id:               item.slug,
          slug:             item.slug,
          title:            item.title,
          shortDescription: stripHtml(item.short_description || item.shortDescription || ''),
          image:            item.image_url || item.main_image_url || SLUG_FALLBACK[item.slug] || imgFlanges,
        })));
        setLoading(false);
      })
      .catch(err => { if (err.name !== 'AbortError') setLoading(false); });

    return () => controller.abort();
  }, [i18n.language]);

  /* ── responsive visible count ────────────────────────────────── */
  useEffect(() => {
    const onResize = () => setVisibleCount(getVisibleCount());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  /* ── card pixel width from viewport ref ─────────────────────── */
  useEffect(() => {
    const measure = () => {
      if (viewportRef.current) setCardWidth(viewportRef.current.offsetWidth / visibleCount);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [visibleCount, products]);

  /* reset index when product list changes */
  useEffect(() => { setCurrentIndex(0); }, [products]);

  /* ── navigation ──────────────────────────────────────────────── */
  const maxIndex = Math.max(0, products.length - visibleCount);

  const goNext = useCallback(() =>
    setCurrentIndex(i => (i >= maxIndex ? 0 : i + 1)), [maxIndex]);

  const goPrev = useCallback(() =>
    setCurrentIndex(i => (i <= 0 ? maxIndex : i - 1)), [maxIndex]);

  /* keep autoplay cb fresh */
  useEffect(() => { autoPlayCb.current = goNext; }, [goNext]);

  /* auto-play every 3.5s, paused while hovered */
  useEffect(() => {
    const id = setInterval(() => {
      if (!isHovering.current) autoPlayCb.current?.();
    }, 3500);
    return () => clearInterval(id);
  }, []);

  if (loading) return null;

  const isRtl = i18n.language === 'ar';

  return (
    <section
      className={styles.specialtiesSection}
      onMouseEnter={() => { isHovering.current = true; }}
      onMouseLeave={() => { isHovering.current = false; }}
    >
      {/* ── Prev arrow ── */}
      <button
        className={`${styles.navBtn} ${styles.prevBtn}`}
        onClick={isRtl ? goNext : goPrev}
        aria-label="Previous"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>

      {/* ── Carousel viewport ── */}
      <div ref={viewportRef} className={styles.carouselViewport}>
        <div
          className={styles.carouselTrack}
          style={{ transform: `translateX(-${currentIndex * cardWidth}px)` }}
        >
          {products.map(product => (
            <div
              key={product.id}
              className={styles.slide}
              style={{ width: cardWidth }}
            >
              <div className={styles.card}>
                <div className={styles.imageWrapper}>
                  <img src={product.image} alt={product.title} className={styles.image} />
                  <div className={styles.imageOverlay}></div>
                </div>
                <div className={styles.content}>
                  <h3 className={styles.title}>{product.title}</h3>
                  <p className={styles.description}>{product.shortDescription}</p>
                  <Link to={`/products/${product.id}`} className={styles.seeMoreBtn}>
                    <span>{t('specialties.seeMore')}</span>
                    <svg viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Next arrow ── */}
      <button
        className={`${styles.navBtn} ${styles.nextBtn}`}
        onClick={isRtl ? goPrev : goNext}
        aria-label="Next"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>

      {/* ── Dot indicators ── */}
      {maxIndex > 0 && (
        <div className={styles.dots}>
          {Array.from({ length: maxIndex + 1 }).map((_, i) => (
            <button
              key={i}
              className={`${styles.dot} ${i === currentIndex ? styles.dotActive : ''}`}
              onClick={() => setCurrentIndex(i)}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default Specialties;
